<template>
  <b-col
    cols="12"
    lg="6"
    class="mb-4"
  >
    <b-card>
      <template #header>
        <h4 class="mb-0">
          👥 Top Clientes
        </h4>
      </template>
      <div class="top-clientes-section">
        <div
          v-if="loading"
          class="text-center p-4"
        >
          <b-spinner />
          <p class="mt-2 mb-0">
            Carregando top clientes...
          </p>
        </div>
        <div
          v-else-if="topClientes.length === 0"
          class="text-center text-muted p-4"
        >
          <feather-icon
            icon="UsersIcon"
            size="48"
            class="text-muted mb-3"
          />
          <h5>Nenhum cliente encontrado</h5>
          <p>Os dados dos melhores clientes aparecerão aqui quando disponíveis.</p>
        </div>
        <div
          v-else
          class="top-clientes-scroll-container"
        >
          <div
            v-for="(cliente, index) in topClientes"
            :key="`${cliente.cliente_store_id || cliente.id || 'cliente'}-${index}`"
            class="d-flex align-items-center justify-content-between mb-2 p-2 rounded border cliente-card"
            style="transition: all 0.2s ease;"
            @mouseover="$event.target.style.transform='translateY(-1px)'"
            @mouseleave="$event.target.style.transform='translateY(0)'"
          >
            <div class="d-flex align-items-center">
              <div class="position-relative mr-2">
                <b-avatar
                  :src="`https://api.dicebear.com/7.x/thumbs/svg?seed=${getClienteName(cliente)}`"
                  size="40"
                  class="border"
                />
                <b-badge
                  :variant="getTopClienteBadgeVariant(index)"
                  pill
                  class="position-absolute top-badge"
                  style="top: -5px; right: -5px; font-size: 10px;"
                >
                  {{ index + 1 }}º
                </b-badge>
              </div>
              <div>
                <h6 class="mb-1 font-weight-bold">
                  {{ getClienteName(cliente) }}
                </h6>
                <small class="text-muted d-block">
                  <span v-if="cliente.email">{{ cliente.email }} • </span>
                  Ticket médio: R$ {{ formatarMoeda(cliente.ticket_medio) }}
                </small>
                <small class="text-muted">
                  Último pedido: {{ formatarData(cliente.ultimo_pedido) }}
                </small>
              </div>
            </div>
            <div class="text-right">
              <div class="font-weight-bold text-success h5 mb-1">
                R$ {{ formatarMoeda(cliente.valor_total_gasto) }}
              </div>
              <small class="text-muted">Total gasto</small>
            </div>
          </div>
        </div>
      </div>
    </b-card>
  </b-col>
</template>

<script>
import {
  BCol, BCard, BSpinner, BAvatar, BBadge,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { formatarMoeda } from '../services/dashboardService'

export default {
  name: 'TopClientes',
  components: {
    BCol,
    BCard,
    BSpinner,
    BAvatar,
    BBadge,
    FeatherIcon,
  },
  props: {
    topClientes: {
      type: Array,
      required: true,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    formatarMoeda(valor) {
      return formatarMoeda(valor)
    },

    getTopClienteBadgeVariant(index) {
      if (index === 0) return 'warning' // 1º lugar - dourado
      if (index === 1) return 'secondary' // 2º lugar - prata
      if (index === 2) return 'danger' // 3º lugar - bronze
      return 'primary' // demais posições
    },

    getClienteName(cliente) {
      // Priorizar o campo 'name' (em inglês) sobre 'nome' (em português)
      const nomeCliente = cliente.name || cliente.nome

      // Se não tem nome ou o nome parece ser um ID (começa com C seguido de números), usar um fallback
      if (!nomeCliente || nomeCliente.match(/^C\d+$/)) {
        // Se tem email, usar a parte antes do @
        if (cliente.email) {
          return cliente.email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        }
        // Se tem telefone, usar "Cliente" + últimos 4 dígitos
        if (cliente.telefone) {
          const digits = cliente.telefone.replace(/\D/g, '')
          return `Cliente ${digits.slice(-4)}`
        }
        // Fallback genérico
        return 'Cliente'
      }
      // Se o nome parece ser real, usar ele
      return nomeCliente
    },

    formatarData(data) {
      if (!data) return 'N/A'

      try {
        let date

        // Se for string, tentar parsear (formato ISO: "2025-09-05T20:18:29.000Z")
        if (typeof data === 'string') {
          date = new Date(data)
        } else if (data instanceof Date) {
          date = data
        } else {
          return 'Formato inválido'
        }

        // Verificar se a data é válida
        if (isNaN(date.getTime())) {
          return 'Data inválida'
        }

        // Formatar para padrão brasileiro
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      } catch (error) {
        console.error('Erro ao formatar data:', error, data)
        return 'Erro na data'
      }
    },
  },
}
</script>

<style scoped>
.top-clientes-section {
  min-height: 200px;
}

.top-clientes-scroll-container {
  max-height: 400px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

.top-clientes-scroll-container::-webkit-scrollbar {
  width: 8px;
}

.top-clientes-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.top-clientes-scroll-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.top-clientes-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.cliente-card {
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.cliente-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background-color: #f8f9fa;
}

.top-badge {
  font-weight: bold;
  font-size: 10px;
}

/* Dark Mode */
.dark-layout {
  .cliente-card {
    background-color: #283046;
    border-color: #3b4253;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }

  .cliente-card:hover {
    background-color: #2c3a50;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }
}
</style>
